import { FormControlLabel, Switch } from "@mui/material";
import { useCallback, type ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useUpdatePreferredTeam } from "../../../../api/teams";
import { useSnackbar } from "../../../../shared/components/snackbar/Snackbar";
import {
  setPermissionsTeamId,
  setPreferredTeamId,
} from "../../../../store/PermissionsSlice";
import type { RootState } from "../../../../store/store";
import { setActiveTeamId } from "../../../../utils/active-team/active-team";

interface PreferredTeamSelectorProps {
  currentTeamId: string;
}

const PreferredTeamSelector = ({
  currentTeamId,
}: PreferredTeamSelectorProps): JSX.Element => {
  const { t: translate } = useTranslation(["profile", "common"]);
  const { showSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const preferredTeamId = useSelector(
    (state: RootState) => state.permissions.preferredTeamId
  );
  const { mutateAsync: updatePreferredTeam, isLoading: isSaving } =
    useUpdatePreferredTeam();

  const isPreferred =
    Boolean(currentTeamId) && currentTeamId === preferredTeamId;
  const isDisabled = isPreferred || isSaving || !currentTeamId;

  const setCurrentTeamAsPreferred = useCallback(async (): Promise<void> => {
    if (isPreferred || isSaving || !currentTeamId) {
      return;
    }

    try {
      await updatePreferredTeam({ teamId: currentTeamId });
      setActiveTeamId(currentTeamId);
      dispatch(setPermissionsTeamId(currentTeamId));
      dispatch(setPreferredTeamId(currentTeamId));
      showSnackbar(
        translate("profiles.currentTeamSelected.preferredTeamSuccess")
      );
    } catch {
      showSnackbar(
        translate("common:apiResponse.unexpectedErrorOccurred"),
        true
      );
    }
  }, [
    currentTeamId,
    dispatch,
    isPreferred,
    isSaving,
    showSnackbar,
    translate,
    updatePreferredTeam,
  ]);

  const handleChange = useCallback(
    (_event: ChangeEvent<HTMLInputElement>, checked: boolean): void => {
      if (!checked) {
        return;
      }

      void setCurrentTeamAsPreferred();
    },
    [setCurrentTeamAsPreferred]
  );

  return (
    <FormControlLabel
      sx={{
        "&.Mui-disabled .MuiFormControlLabel-label": {
          color: "text.primary",
        },
      }}
      label={translate("profiles.currentTeamSelected.setAsPreferredTeamToLogin")}
      control={
        <Switch
          checked={isPreferred}
          onChange={handleChange}
          color="primary"
          disabled={isDisabled}
          sx={{
            "& .MuiSwitch-switchBase.Mui-checked.Mui-disabled": {
              color: "primary.main",
            },
            "& .MuiSwitch-switchBase.Mui-checked.Mui-disabled + .MuiSwitch-track":
              {
                backgroundColor: "primary.main",
                opacity: 0.5,
              },
          }}
          inputProps={{
            "aria-label": "set-as-preferred-team-to-login-switch",
          }}
        />
      }
    />
  );
};

export default PreferredTeamSelector;
